import { grpc } from '@improbable-eng/grpc-web';
import { ContentState, EditorState, EntityInstance } from 'draft-js';

import { durationObjectToDuration, floatToDuration } from './helpers';

import bytestream_pb from '@gen/proto/google/bytestream/bytestream_pb';
import bytestream_pb_grpc, { ByteStreamClient } from '@gen/proto/google/bytestream/bytestream_pb_service';
import transcript_pb from '@gen/proto/lr/transcript/v1alpha1/transcript_pb';
import transcript_pb_grpc, { ServiceError } from '@gen/proto/lr/transcript/v1alpha1/transcript_pb_service';

import { TRANSCRIPT_SERVICE_HOST } from './config';
import { BlockInfo, WordInfo } from './models';
import * as draftUtils from './draft-utils';
import { TranscriptExportFormat } from './types';
import { FieldMask } from 'google-protobuf/google/protobuf/field_mask_pb';

interface Props {
  getIdToken: () => string | undefined;
}

type EntityObject = {
  entityKey: string;
  blockKey: string;
  entity: EntityInstance;
  start: number;
  end: number;
};

export default class TranscriptService {
  private client: transcript_pb_grpc.TranscriptServiceClient;
  private bsClient: ByteStreamClient;
  private getIdToken: () => string | undefined;

  constructor(props: Props) {
    this.client = new transcript_pb_grpc.TranscriptServiceClient(TRANSCRIPT_SERVICE_HOST, {
      transport: grpc.FetchReadableStreamTransport({}),
    });
    this.bsClient = new ByteStreamClient(TRANSCRIPT_SERVICE_HOST, {
      transport: grpc.WebsocketTransport(),
    });
    this.getIdToken = props.getIdToken;
  }

  watchTranscripts = (
    req: transcript_pb.WatchTranscriptsRequest,
  ): transcript_pb_grpc.ResponseStream<transcript_pb.WatchTranscriptsResponse> => {
    const md = new grpc.Metadata(
      this.getIdToken()
        ? {
            Authorization: `Bearer ${this.getIdToken()}`,
          }
        : {},
    );
    return this.client.watchTranscripts(req, md);
  };

  listTranscripts = (req: transcript_pb.ListTranscriptsRequest): Promise<transcript_pb.Transcript[]> => {
    return new Promise((resolve, reject) => {
      const md = new grpc.Metadata(
        this.getIdToken()
          ? {
              Authorization: `Bearer ${this.getIdToken()}`,
            }
          : {},
      );
      this.client.listTranscripts(req, md, (error, msg) => {
        if (error) {
          reject(error.message);
        } else if (msg) {
          resolve(msg.getTranscriptsList());
        }
      });
    });
  };

  loadSegmentsFromRemote = (name: string, version?: string): Promise<transcript_pb.Transcript> => {
    return new Promise((resolve, reject) => {
      const req = new transcript_pb.GetTranscriptRequest();
      req.setName(name);
      if (version) {
        req.setVersion(version);
      }
      this.client.getTranscript(
        req,
        new grpc.Metadata(
          this.getIdToken()
            ? {
                Authorization: `Bearer ${this.getIdToken()}`,
              }
            : {},
        ),
        (error, msg) => {
          if (error) {
            console.error('get_transcript_error', error);
            reject(error.message);
          } else if (msg) {
            console.debug('get_transcript_success');
            resolve(msg);
          }
        },
      );
    });
  };

  createTranscript = (duration: number, metadata: transcript_pb.TranscriptMetadata.AsObject): Promise<string> => {
    return new Promise((resolve, reject) => {
      const md = new transcript_pb.TranscriptMetadata();
      const subject = !metadata.subject ? 'Ónefnt diktat' : metadata.subject;
      md.setSubject(subject);
      md.setDictation(true);
      md.setFileType(metadata.fileType);
      md.setDescription(metadata.description);
      md.setLanguageCode(metadata.languageCode);
      md.setRecordingDuration(floatToDuration(duration));

      const transcript = new transcript_pb.Transcript();
      transcript.setMetadata(md);

      const req = new transcript_pb.CreateTranscriptRequest();
      req.setTranscript(transcript);

      this.client.createTranscript(
        req,
        new grpc.Metadata({ Authorization: `Bearer ${this.getIdToken()}` }),
        (error: ServiceError | null, msg: transcript_pb.CreateTranscriptResponse | null) => {
          if (error) {
            console.error('upl error creating transcript', error);
            reject(error);
          } else if (msg) {
            console.debug(`upl successfully created new transcript '${msg.getName()}'`);
            resolve(msg.getName());
          }
        },
      );
    });
  };

  private contentStateToSegmentsProto = (contentState: ContentState): transcript_pb.Transcript.TranscriptSegment[] => {
    return contentState.getBlocksAsArray().map((block) => {
      let selectedEntity: {
        entityKey: string;
        blockKey: string;
        entity: EntityInstance;
      };

      const entities: EntityObject[] = [];
      block.findEntityRanges(
        (character) => {
          if (character.getEntity() !== null) {
            selectedEntity = {
              entityKey: character.getEntity(),
              blockKey: block.getKey(),
              entity: contentState.getEntity(character.getEntity()),
            };
            return true;
          }
          return false;
        },
        (start, end) => {
          entities.push({ ...selectedEntity, start, end });
        },
      );

      const segment = new transcript_pb.Transcript.TranscriptSegment();
      const { speakerId, startTime, endTime } = block.getData().toObject() as {
        speakerId: string;
        startTime: number;
        endTime: number;
      };
      if (startTime && endTime) {
        segment.setStartTime(floatToDuration(startTime));
        segment.setEndTime(floatToDuration(endTime));
      }
      if (speakerId) {
        segment.setSpeakerId(speakerId);
      }

      // const text = block.getText();
      entities.forEach((entityObj: EntityObject) => {
        const { entity } = entityObj;
        const { data } = entity.getData() as { data: WordInfo[] };
        try {
          data.forEach((word: WordInfo) => {
            const wordInfo = new transcript_pb.Transcript.TranscriptSegment.WordInfo();
            wordInfo.setWord(word.word);
            wordInfo.setStartTime(floatToDuration(word.startTime));
            wordInfo.setEndTime(floatToDuration(word.endTime));
            segment.addWords(wordInfo);
          });
        } catch (error) {
          console.debug('error updating transcript in block ', block.getKey());
        }
      });
      return segment;
    });
  };

  private contentBlocksToSegmentsProto = (contentState: ContentState): transcript_pb.Transcript.TranscriptSegment[] => {
    return contentState.getBlocksAsArray().map((block) => {
      const segment = new transcript_pb.Transcript.TranscriptSegment();
      const blockData = block.getData().toObject() as BlockInfo;
      if (blockData.speakerId) {
        segment.setSpeakerId(blockData.speakerId);
      }

      const entityKey = draftUtils.getBlockEntity(block);
      const entity = entityKey ? contentState.getEntity(entityKey) : undefined;

      if (entity) {
        const { data } = entity.getData() as { data: WordInfo[] };
        try {
          data.forEach((word: WordInfo, i: number) => {
            const wordInfo = new transcript_pb.Transcript.TranscriptSegment.WordInfo();
            wordInfo.setWord(word.word);
            wordInfo.setStartTime(floatToDuration(word.startTime));
            wordInfo.setEndTime(floatToDuration(word.endTime));
            segment.addWords(wordInfo);
          });
          if (blockData.startTime && blockData.endTime) {
            segment.setStartTime(floatToDuration(blockData.startTime));
            segment.setEndTime(floatToDuration(blockData.endTime));
          }
        } catch (error) {
          console.debug('error updating transcript in block ', block.getKey());
        }
      }
      return segment;
    });
  };

  updateTranscriptMetadata = (
    name: string,
    metadata: transcript_pb.TranscriptMetadata.AsObject,
  ): Promise<transcript_pb.Transcript> => {
    return new Promise((resolve, reject) => {
      const req = new transcript_pb.UpdateTranscriptRequest();
      const transcriptDiff = new transcript_pb.Transcript();
      transcriptDiff.setName(name);

      const newMetadata = new transcript_pb.TranscriptMetadata();
      if (metadata.subject) newMetadata.setSubject(metadata.subject);
      if (metadata.description) newMetadata.setDescription(metadata.description);
      if (metadata.keywordsList) newMetadata.setKeywordsList(metadata.keywordsList);
      if (metadata.speakersList) {
        newMetadata.setSpeakersList(
          metadata.speakersList.map((speakerInfo) => {
            const si = new transcript_pb.TranscriptMetadata.SpeakerInfo();
            si.setName(speakerInfo.name);
            si.setOriginalName(speakerInfo.originalName);
            si.setSpeakerId(speakerInfo.speakerId);
            return si;
          }),
        );
      }
      transcriptDiff.setMetadata(newMetadata);

      req.setTranscript(transcriptDiff);

      const mask = new FieldMask();
      mask.setPathsList(['metadata.subject', 'metadata.description', 'metadata.keywords', 'metadata.speakers']);
      req.setUpdateMask(mask);

      this.client.updateTranscript(
        req,
        new grpc.Metadata(
          this.getIdToken()
            ? {
                Authorization: `Bearer ${this.getIdToken()}`,
              }
            : {},
        ),
        (error, transcript) => {
          if (error) {
            reject(error.message);
          } else if (transcript) {
            resolve(transcript);
          }
        },
      );
    });
  };

  deleteTranscript = (name: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const req = new transcript_pb.DeleteTranscriptRequest();
      req.setName(name);
      const md = new grpc.Metadata(
        this.getIdToken()
          ? {
              Authorization: `Bearer ${this.getIdToken()}`,
            }
          : {},
      );
      this.client.deleteTranscript(req, md, (error) => {
        if (error) {
          reject(error.message);
        } else {
          resolve();
        }
      });
    });
  };

  updateTranscript = (
    transcript: transcript_pb.Transcript,
    editorState: EditorState,
    metadata?: transcript_pb.TranscriptMetadata.AsObject,
    annotations?: transcript_pb.Annotation.AsObject[],
  ): Promise<transcript_pb.Transcript> => {
    return new Promise((resolve, reject) => {
      const segmentsList = this.contentBlocksToSegmentsProto(editorState.getCurrentContent());

      const version = transcript.getVersion();
      const req = new transcript_pb.UpdateTranscriptRequest();
      const transcriptDiff = new transcript_pb.Transcript();
      transcriptDiff.setName(transcript.getName());
      transcriptDiff.setSegmentsList(segmentsList);

      if (version) {
        const newVersion = new transcript_pb.Transcript.Version();
        newVersion.setName(version.getName());
        transcriptDiff.setVersion(newVersion);
      }

      if (metadata) {
        const newMetadata = new transcript_pb.TranscriptMetadata();
        if (metadata.subject) {
          newMetadata.setSubject(metadata.subject);
        }
        newMetadata.setDescription(metadata.description);
        newMetadata.setKeywordsList(metadata.keywordsList);
        metadata.speakersMap.forEach(([key, value]) => {
          const speaker = new transcript_pb.SpeakerInfo();
          speaker.setAgeGroup(value.ageGroup);
          speaker.setFullName(value.fullName);
          speaker.setGender(value.gender);
          newMetadata.getSpeakersMap().set(key, speaker);
        });

        transcriptDiff.setMetadata(newMetadata);
      }

      if (annotations) {
        transcriptDiff.setAnnotationsList(
          annotations.map((asObj) => {
            const annotation = new transcript_pb.Annotation();
            annotation.setLabel(asObj.label);
            annotation.setStartTime(durationObjectToDuration(asObj.startTime));
            annotation.setEndTime(durationObjectToDuration(asObj.endTime));
            return annotation;
          }),
        );
      }

      req.setTranscript(transcriptDiff);

      const mask = new FieldMask();
      mask.setPathsList([
        'metadata.subject',
        'metadata.description',
        'metadata.keywords',
        'metadata.speakers',
        'segments',
        'annotations',
      ]);
      req.setUpdateMask(mask);

      this.client.updateTranscript(
        req,
        new grpc.Metadata(
          this.getIdToken()
            ? {
                Authorization: `Bearer ${this.getIdToken()}`,
              }
            : {},
        ),
        (error, transcript) => {
          if (error) {
            reject(`${error.message} (save_edits)`);
          } else if (transcript) {
            console.debug('successfully saved (save_edits)');
            resolve(transcript);
          }
        },
      );
    });
  };

  uploadAudio = (name: string, chunks: ArrayBuffer[], header: ArrayBuffer): Promise<void> => {
    return new Promise((resolve, reject) => {
      try {
        const stream = this.bsClient.write(new grpc.Metadata({ Authorization: `Bearer ${this.getIdToken()}` }));
        stream.on('status', (status: bytestream_pb_grpc.Status) => {
          if (status.code !== grpc.Code.OK) {
            reject(`Error while uploading ${status.details}`);
          }
        });
        stream.on('end', () => {
          resolve();
        });

        const offset = 0;
        const chunksToWrite = [header, ...chunks];
        chunksToWrite.forEach((chunk, idx) => {
          const req = new bytestream_pb.WriteRequest();
          const blobName = `blob/${name.replace('transcripts', 'transcript')}`;
          req.setResourceName(blobName);
          req.setData(new Uint8Array(chunk));
          req.setWriteOffset(offset);
          if (idx === chunksToWrite.length - 1) {
            req.setFinishWrite(true);
          }
          stream.write(req);
        });

        stream.end();
      } catch {
        reject(new Error("No player available, can't upload audio."));
      }
    });
  };

  // Download *saved* transcript as SRT file
  exportTranscript = (
    name: string,
    format: TranscriptExportFormat,
    subject?: string,
    noDownload?: boolean,
  ): Promise<string | undefined> => {
    return new Promise((resolve, reject) => {
      console.debug(`Exporting as ${format}`);

      const req = new transcript_pb.ExportTranscriptRequest();
      req.setName(name);
      if (format === 'srt') {
        req.setFormat(transcript_pb.ExportFormat.SRT);
      } else if (format === 'vtt_paint_on') {
        req.setFormat(transcript_pb.ExportFormat.VTT_PAINT_ON);
      } else if (format === 'smart_srt') {
        req.setFormat(transcript_pb.ExportFormat.SMART_SRT);
      } else {
        req.setFormat(transcript_pb.ExportFormat.AUDACITY_LABELS);
      }

      const md = new grpc.Metadata(
        this.getIdToken()
          ? {
              Authorization: `Bearer ${this.getIdToken()}`,
            }
          : {},
      );

      this.client.exportTranscript(req, md, (error, msg) => {
        if (error) {
          console.error('get_transcript_error', error);
          reject(error.message);
        } else if (msg) {
          const exported = msg.getExportedTranscript();
          if (!exported) {
            return reject('Empty response');
          }

          const blob = new Blob([exported.getData_asU8()]);
          const url = URL.createObjectURL(blob);
          let extension = 'txt';
          if (format === 'srt' || format === 'smart_srt') extension = 'srt';
          else if (format === 'vtt_paint_on') {
            extension = 'vtt';
            resolve(url);
          }
          // URL.revokeObjectURL(url);

          if (!noDownload) {
            // NOTE: this is a little hacky
            const hf = document.createElement('a');
            hf.style.display = 'none';
            hf.href = url;
            hf.download = `${subject ? subject : name}.${extension}`;
            document.body.appendChild(hf);
            hf.click();
            hf.remove();
          }
          resolve(undefined);
        }
      });
    });
  };

  listCollections = (): Promise<transcript_pb.Collection[]> => {
    return new Promise((resolve, reject) => {
      const req = new transcript_pb.ListCollectionsRequest();
      const md = new grpc.Metadata(
        this.getIdToken()
          ? {
              Authorization: `Bearer ${this.getIdToken()}`,
            }
          : {},
      );
      this.client.listCollections(req, md, (error, msg) => {
        if (error) {
          reject(error.message);
        } else if (msg) {
          resolve(msg.getCollectionsList());
        }
      });
    });
  };

  createCollection = (displayName: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      const req = new transcript_pb.CreateCollectionRequest();
      req.setDisplayName(displayName);
      const md = new grpc.Metadata(
        this.getIdToken()
          ? {
              Authorization: `Bearer ${this.getIdToken()}`,
            }
          : {},
      );
      this.client.createCollection(req, md, (error, msg) => {
        if (error) {
          reject(error.message);
        } else if (msg) {
          resolve(msg.getName());
        }
      });
    });
  };

  updateCollection = (name: string, displayName: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const req = new transcript_pb.UpdateCollectionRequest();
      const col = new transcript_pb.Collection();
      col.setName(name);
      col.setDisplayName(displayName);
      req.setCollection(col);

      const mask = new FieldMask();
      mask.setPathsList(['display_name']);
      req.setUpdateMask(mask);

      const md = new grpc.Metadata(
        this.getIdToken()
          ? {
              Authorization: `Bearer ${this.getIdToken()}`,
            }
          : {},
      );
      this.client.updateCollection(req, md, (error) => {
        if (error) {
          reject(error.message);
        } else {
          resolve();
        }
      });
    });
  };

  deleteCollection = (name: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const req = new transcript_pb.DeleteCollectionRequest();
      req.setName(name);

      const md = new grpc.Metadata(
        this.getIdToken()
          ? {
              Authorization: `Bearer ${this.getIdToken()}`,
            }
          : {},
      );
      this.client.deleteCollection(req, md, (error) => {
        if (error) {
          reject(error.message);
        } else {
          resolve();
        }
      });
    });
  };

  getCollectionPermissions = (
    collectionName: string,
  ): Promise<transcript_pb.GetCollectionPermissionsResponse.Actor.AsObject[]> => {
    return new Promise((resolve, reject) => {
      const req = new transcript_pb.GetCollectionPermissionsRequest();
      req.setCollectionName(collectionName);
      const md = new grpc.Metadata(
        this.getIdToken()
          ? {
              Authorization: `Bearer ${this.getIdToken()}`,
            }
          : {},
      );
      this.client.getCollectionPermissions(req, md, (error, msg) => {
        if (error) {
          reject(error.message);
        } else if (msg) {
          resolve(msg.getActorsList().map((a) => a.toObject()));
        }
      });
    });
  };

  setCollectionPermissions = (collectionName: string, actorNames: string[]): Promise<void> => {
    return new Promise((resolve, reject) => {
      const req = new transcript_pb.SetCollectionPermissionsRequest();
      req.setCollectionName(collectionName);
      req.setActorNamesList(actorNames);
      const md = new grpc.Metadata(
        this.getIdToken()
          ? {
              Authorization: `Bearer ${this.getIdToken()}`,
            }
          : {},
      );
      this.client.setCollectionPermissions(req, md, (error) => {
        if (error) {
          reject(error.message);
        } else {
          resolve();
        }
      });
    });
  };

  addTranscriptToCollection = (collectionName: string, transcriptName: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const req = new transcript_pb.AddTranscriptToCollectionRequest();
      req.setCollectionName(collectionName);
      req.setTranscriptName(transcriptName);
      const md = new grpc.Metadata(
        this.getIdToken()
          ? {
              Authorization: `Bearer ${this.getIdToken()}`,
            }
          : {},
      );
      this.client.addTranscriptToCollection(req, md, (error, msg) => {
        if (error) {
          reject(error.message);
        } else {
          resolve();
        }
      });
    });
  };

  removeTranscriptFromCollection = (collectionName: string, transcriptName: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const req = new transcript_pb.RemoveTranscriptFromCollectionRequest();
      req.setCollectionName(collectionName);
      req.setTranscriptName(transcriptName);
      const md = new grpc.Metadata(
        this.getIdToken()
          ? {
              Authorization: `Bearer ${this.getIdToken()}`,
            }
          : {},
      );
      this.client.removeTranscriptFromCollection(req, md, (error, msg) => {
        if (error) {
          reject(error.message);
        } else {
          resolve();
        }
      });
    });
  };

  listCollectionTranscripts = (collectionName: string): Promise<transcript_pb.Transcript[]> => {
    return new Promise((resolve, reject) => {
      const req = new transcript_pb.ListCollectionTranscriptsRequest();
      req.setCollectionName(collectionName);
      const md = new grpc.Metadata(
        this.getIdToken()
          ? {
              Authorization: `Bearer ${this.getIdToken()}`,
            }
          : {},
      );
      this.client.listCollectionTranscripts(req, md, (error, msg) => {
        if (error) {
          reject(error.message);
        } else if (msg) {
          resolve(msg.getTranscriptsList());
        }
      });
    });
  };
}
